import { Component, OnInit, OnDestroy, Inject, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatSnackBar, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from 'src/app/common/components/base/base.component';
import { SideChannelMenuComponent } from 'src/app/common/components/side-channel-menu/side-channel-menu.component';
import { ClientConfigService } from 'src/app/common/services/client-config.service';
import { FunctionsService } from 'src/app/common/services/functions.service';
import { LoginService } from 'src/app/managers/login.service';
import { Category } from 'src/app/models/category';
import { Subscription } from 'rxjs';
import { RssFeedService } from 'src/app/managers/rssFeed.service';
import { CoreResponseMessage, CoreResponseStatus } from 'src/app/contracts/responseMessage/coreResponseMessage';

@Component({
  selector: 'app-edit-category',
  templateUrl: './edit-category.component.html',
  styleUrls: ['./edit-category.component.css']
})
export class EditCategoryComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('categoryNameField', { static: true }) categoryElement: ElementRef;
  category: Category;
  updatedCategories: Category[];
  categoryControl = new FormControl('', Validators.required);
  categoryControlSubscription: Subscription;
  selectedId: number;

  constructor(route: ActivatedRoute, loginManager: LoginService, public clientConfig: ClientConfigService, dialog: MatDialog,
              private _functionsService: FunctionsService, private _snackBar: MatSnackBar, public dialogRef: MatDialogRef<SideChannelMenuComponent>,
              @Inject(MAT_DIALOG_DATA) public data: any, private _rssFeedService: RssFeedService) { 
    super(route, loginManager, clientConfig, dialog);
  }

  ngOnInit() {
    super.ngOnInit();
    this.updatedCategories = [];

    /*
    Deepcopy data because data from calling component is reference to original data which will take effect when it's changed here.
    */
    this.dataSource = this._functionsService.deepCopy<Category[]>(this.data.dataSource);

    if (this.data.action === 'Add')
      this.category = new Category();
    else {
      if (this.dataSource.length > 0) {
        this.category = this._functionsService.deepCopy<Category>(this.dataSource[0]);
        this.selectedId = this.category.Id;
      }
      else {
        this.category = new Category();
        this.selectedId = 0;
      }
    }

    this.categoryControlSubscription = this.categoryControl.valueChanges.subscribe(x => {
        if (this.dataSource.length > 0) {
          this.category = this._functionsService.deepCopy(this.dataSource.filter(c => c.Id === this.categoryControl.value)[0]);
        }
    });
  }

  ngAfterViewInit() {
    if (this.data.action === 'Add') {
      // setTimeout(() => {
      //   this.categoryElement.nativeElement.focus();
      // }, 100);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.categoryControlSubscription.unsubscribe();
  }

  enableActionButton(): boolean {
    if (this.data.action === 'Add') {
      if (this.category !== undefined && this.category.Name !== undefined) {
        return this.category.Name.length > 0 ? true : false;
      }
      else
        return false;
    }
    else if (this.data.action === 'Edit') {
      if (this.category !== undefined && this.category.Name !== undefined) {
        return (this.selectedId > 0 && this.category.Name.length > 0) ? true : false;
      }
      else
        return false;
    }
    else if (this.data.action === 'Remove') {
      return this.selectedId > 0 ? true : false;
    }
  }

  async actionClick() {
    if (this.data.action === 'Add') {
      if (this.isDupeCategoryName()) {
        let alertStr = "Can't add the group because the same name already exists";
        this.openAlertDialog('Add Group', alertStr, '500px', '200px');
      }
      else
        this.addCategory();
    }
    else if (this.data.action === 'Edit') {
      if (this.isDupeCategoryName()) {
        let alertStr = 'The group name "' + this.category.Name + '" already exists. Choose other name';
        this.openAlertDialog('Edit Group', alertStr, '500px', '200px');
      }
      else
        this.editCategory();
    }
    else if (this.data.action === 'Remove') {
      if (this.category.Channels.length > 0) {
        let alertStr = "Can't delete the group because there is a subscription belonging to it";
        this.openAlertDialog('Remove Group', alertStr, '500px', '200px');
      }  
      else
        this.removeCategory();
    }
  }

  closeClick() {
    this.dialogRef.close(this.updatedCategories);
  }

  private isDupeCategoryName(): boolean {
    return this.dataSource.filter(c => c.Name.toLowerCase() === this.category.Name.toLowerCase()).length > 0 ? true : false;
  }

  private async addCategory() {
    let responseMsg: CoreResponseMessage;

    this._snackBar.open('Adding...');
    responseMsg = await this._rssFeedService.addCategoryAsync(this.getLoggedinUser(), this.category);
    this._snackBar.dismiss();

    if (responseMsg.Status === CoreResponseStatus.OK) {
      this._snackBar.open('The group has been added.', 'OK', { duration: this._successMsgDuration });
      
      if (this._functionsService.isNullOrUndefined(this.category.Channels))
        this.category.Channels = [];

      let insertedCategory = responseMsg.Result[0];
      this.category.Id = insertedCategory.Id;

      this.updatedCategories.push(this._functionsService.deepCopy<Category>(this.category));
      this.dataSource.push(this._functionsService.deepCopy<Category>(this.category));
      this.category = new Category();
    }
    else {
      this._snackBar.open('Failed to add the group: ' + responseMsg.Message, 'OK', { duration: this._failMsgDuration });
      this.handleCoreResponseMessageError(responseMsg);
    }
  }

  private async editCategory() {
    let responseMsg: CoreResponseMessage;

    this._snackBar.open('Updating...');
    responseMsg = await this._rssFeedService.updateCategoryAsync(this.getLoggedinUser(), this.category);
    this._snackBar.dismiss();

    if (responseMsg.Status === CoreResponseStatus.OK) {
      this._snackBar.open('The group has been updated.', 'OK', { duration: this._successMsgDuration });
      this.updatedCategories.push(this._functionsService.deepCopy<Category>(this.category));

      for (let index = 0; index < this.dataSource.length; index++) {
        if (this.dataSource[index].Id === this.category.Id) {
          this.dataSource[index].Name = this.category.Name;
          this.dataSource[index].Description = this.category.Description;
        }
      }
    }
    else {
      this._snackBar.open('Failed to update the group: ' + responseMsg.Message, 'OK', { duration: this._failMsgDuration });
      this.handleCoreResponseMessageError(responseMsg);
    }
  }

  private async removeCategory() {
    let responseMsg: CoreResponseMessage;

    this._snackBar.open('Removing...');
    responseMsg = await this._rssFeedService.removeCategoryAsync(this.getLoggedinUser(), this.category);
    this._snackBar.dismiss();

    if (responseMsg.Status === CoreResponseStatus.OK) {
      this._snackBar.open('The group ' + this.category.Name + ' has been removed.', 'OK', { duration: this._successMsgDuration });
      this.updatedCategories.push(this._functionsService.deepCopy<Category>(this.category));
      this.dataSource = this.dataSource.filter(c => c.Id !== this.category.Id);

      if (this.dataSource.length > 0) {
        this.category = this._functionsService.deepCopy<Category>(this.dataSource[0]);
        this.selectedId = this.category.Id;
      }
      else {
        this.category = new Category();
        this.selectedId = 0;
      }
    }
    else {
      this._snackBar.open('Failed to remove the group: ' + responseMsg.Message, 'OK', { duration: this._successMsgDuration });
      this.handleCoreResponseMessageError(responseMsg);
    }
  }
}